import { HostType } from '@repo/types/host';
import { getHostContextFromRequest, type ResolvedHostContext } from '../host/host-context';
import type { CookieReconcileContext, CookieReconcileResult } from '../utils/cookie-reconciliation';
import {
    buildCookieHeader,
    extractSetCookieHeaders,
    parseCookieHeader,
    setCookieInHeader,
} from '../utils/cookie-utils';
import { SESSION_COOKIE_NAME, TOKEN_BINDING_COOKIE_NAME, TOKEN_COOKIE_NAME } from './constants';

export { SESSION_COOKIE_NAME, TOKEN_BINDING_COOKIE_NAME, TOKEN_COOKIE_NAME } from './constants';

/** Whether routine session→token cookie exchange should run for this host context. */
export function shouldExchangeSessionTokenCookies(ctx: Pick<ResolvedHostContext, 'hostType'>): boolean {
    return ctx.hostType === HostType.WEB;
}

const COOKIE_EXPIRE = 'Thu, 01 Jan 1970 00:00:00 GMT';

export type TokenExchangeSuccess = {
    ok: true;
    token: string;
    setCookieHeaders: string[];
    response: Response;
};

type TokenExchangeFailure = {
    ok: false;
    error: string;
};

export type TokenExchangeResult = TokenExchangeSuccess | TokenExchangeFailure;

const routineExchangePromises = new Map<string, Promise<TokenExchangeResult>>();

function fingerprintSession(session: string): string {
    let hash = 2166136261;
    for (let i = 0; i < session.length; i++) {
        hash ^= session.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Binding cookie value for a Laravel session id (used when writing the Next cookie store). */
export function tokenBindingValue(session: string): string {
    return fingerprintSession(session);
}

function isTokenBoundToSession(cookies: Map<string, string>): boolean {
    const session = cookies.get(SESSION_COOKIE_NAME);
    if (!session) {
        return false;
    }

    const binding = cookies.get(TOKEN_BINDING_COOKIE_NAME);
    if (!binding) {
        return false;
    }

    return binding === fingerprintSession(session);
}

export function isTokenSynced(cookies: Map<string, string>): boolean {
    return cookies.has(TOKEN_COOKIE_NAME) && isTokenBoundToSession(cookies);
}

export function hasOrphanedToken(cookies: Map<string, string>): boolean {
    if (cookies.has(SESSION_COOKIE_NAME)) {
        return false;
    }

    return cookies.has(TOKEN_COOKIE_NAME) || cookies.has(TOKEN_BINDING_COOKIE_NAME);
}

/**
 * Whether the app is on the shared auth client and the legacy `token` is not bound to this request's site
 * session. The worker no longer re-exchanges in that state, so a token minted for an earlier session (for
 * example another user's, left behind by a logout that did not clear it) would keep authenticating the
 * app; it is cleared instead. Callers apply the web-host gate.
 */
export function hasStaleBoundToken(cookies: Map<string, string>, context?: CookieReconcileContext): boolean {
    return context?.authClientEnabled === true && cookies.has(TOKEN_COOKIE_NAME) && !isTokenBoundToSession(cookies);
}

function buildTokenBindingSetCookieHeader(session: string): string {
    return `${TOKEN_BINDING_COOKIE_NAME}=${fingerprintSession(session)}; Path=/`;
}

function buildOrphanedTokenClearCookieHeaders(): string[] {
    return [
        `${TOKEN_COOKIE_NAME}=; Path=/; Expires=${COOKIE_EXPIRE}; Max-Age=0`,
        `${TOKEN_BINDING_COOKIE_NAME}=; Path=/; Expires=${COOKIE_EXPIRE}; Max-Age=0`,
    ];
}

/**
 * Clears a stale legacy token in both scopes it is written with: host-only by the worker exchange and
 * `Domain=<host>` by Laravel. The binding cookie is only ever written host-only.
 */
function buildStaleTokenClearCookieHeaders(host: string): string[] {
    return [
        ...buildOrphanedTokenClearCookieHeaders(),
        `${TOKEN_COOKIE_NAME}=; Path=/; Domain=${host}; Expires=${COOKIE_EXPIRE}; Max-Age=0`,
    ];
}

function stripOrphanedTokenFromCookieHeader(cookieHeader: string): string {
    const cookies = parseCookieHeader(cookieHeader);
    cookies.delete(TOKEN_COOKIE_NAME);
    cookies.delete(TOKEN_BINDING_COOKIE_NAME);
    return buildCookieHeader(cookies);
}

function appendTokenToCookieHeader(existingHeader: string, token: string): string {
    return setCookieInHeader(existingHeader, TOKEN_COOKIE_NAME, token);
}

function appendTokenExchangeCookies(session: string, token: string, setCookieHeaders: string[]): string[] {
    const headers = [...setCookieHeaders];

    if (!headers.some(header => header.startsWith(`${TOKEN_COOKIE_NAME}=`))) {
        headers.push(`${TOKEN_COOKIE_NAME}=${token}; Path=/`);
    }

    if (!headers.some(header => header.startsWith(`${TOKEN_BINDING_COOKIE_NAME}=`))) {
        headers.push(buildTokenBindingSetCookieHeader(session));
    }

    return headers;
}

/**
 * Whether middleware/worker should exchange on this request. Re-exchanges when session
 * exists but token is missing or not bound to the current session.
 *
 * For intentional refresh (401 retry, `refreshTokenAction({ force: true })`, login follow-up),
 * call {@link exchangeSessionForToken} directly instead.
 */
function shouldPerformRoutineTokenExchange(cookies: Map<string, string>): boolean {
    if (!cookies.has(SESSION_COOKIE_NAME)) {
        return false;
    }

    if (!cookies.has(TOKEN_COOKIE_NAME)) {
        return true;
    }

    return !isTokenBoundToSession(cookies);
}

/**
 * Routine request-boundary exchange (middleware/worker). Returns `null` when exchange
 * should be skipped (no session, token synced to session, or orphaned token cleanup only).
 */
async function exchangeSessionForTokenIfNeeded(
    cookieHeader: string,
    wandWebsiteUrl: string,
): Promise<TokenExchangeResult | null> {
    const cookies = parseCookieHeader(cookieHeader);
    if (!shouldPerformRoutineTokenExchange(cookies)) {
        return null;
    }

    return exchangeSessionForTokenDeduped(cookieHeader, wandWebsiteUrl);
}

/** Coalesces parallel routine exchanges for the same session within one worker/middleware isolate. */
async function exchangeSessionForTokenDeduped(
    cookieHeader: string,
    wandWebsiteUrl: string,
): Promise<TokenExchangeResult> {
    const session = parseCookieHeader(cookieHeader).get(SESSION_COOKIE_NAME);
    if (!session) {
        return exchangeSessionForToken(cookieHeader, wandWebsiteUrl);
    }

    const key = `${wandWebsiteUrl}:${session}`;
    const existing = routineExchangePromises.get(key);
    if (existing) {
        return existing;
    }

    const promise = exchangeSessionForToken(cookieHeader, wandWebsiteUrl).finally(() => {
        if (routineExchangePromises.get(key) === promise) {
            routineExchangePromises.delete(key);
        }
    });
    routineExchangePromises.set(key, promise);
    return promise;
}

/**
 * Reconcile auth cookies at a request boundary: clear orphaned tokens, exchange when
 * session/token binding is stale, and produce Set-Cookie headers for the response.
 *
 * When the app is on the shared auth client, new sign-ins start in the package, so the worker no
 * longer exchanges the site session through the shim, and a legacy token bound to an earlier session is
 * cleared instead of re-exchanged. The orphan clear does not depend on that: the legacy token is bound to
 * the site session in both states.
 */
export async function reconcileAuthCookiesForRequest(
    request: Pick<Request, 'url' | 'headers'>,
    cookieHeader: string,
    wandWebsiteUrl: string,
    context?: CookieReconcileContext,
): Promise<CookieReconcileResult> {
    if (!shouldExchangeSessionTokenCookies(getHostContextFromRequest(request))) {
        return {
            downstreamCookieHeader: cookieHeader,
            setCookieHeaders: [],
        };
    }

    const cookies = parseCookieHeader(cookieHeader);

    if (hasOrphanedToken(cookies)) {
        return {
            downstreamCookieHeader: stripOrphanedTokenFromCookieHeader(cookieHeader),
            setCookieHeaders: buildOrphanedTokenClearCookieHeaders(),
        };
    }

    if (context?.authClientEnabled) {
        if (hasStaleBoundToken(cookies, context)) {
            return {
                downstreamCookieHeader: stripOrphanedTokenFromCookieHeader(cookieHeader),
                setCookieHeaders: buildStaleTokenClearCookieHeaders(new URL(request.url).hostname),
            };
        }

        return {
            downstreamCookieHeader: cookieHeader,
            setCookieHeaders: [],
        };
    }

    const session = cookies.get(SESSION_COOKIE_NAME);
    const result = await exchangeSessionForTokenIfNeeded(cookieHeader, wandWebsiteUrl);
    if (!result?.ok || !session) {
        return {
            downstreamCookieHeader: cookieHeader,
            setCookieHeaders: [],
        };
    }

    const downstreamCookieHeader = appendTokenToCookieHeader(cookieHeader, result.token);
    const setCookieHeaders = appendTokenExchangeCookies(session, result.token, [...result.setCookieHeaders]);

    return {
        downstreamCookieHeader,
        setCookieHeaders,
    };
}

/**
 * Always attempts session→token exchange. Use for explicit refresh paths such as
 * `refreshTokenAction({ force: true })`, and post-login cookie sync — not for middleware/worker.
 */
export async function exchangeSessionForToken(
    cookieHeader: string,
    wandWebsiteUrl: string,
): Promise<TokenExchangeResult> {
    try {
        const response = await fetch(`${wandWebsiteUrl}/account/token`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Cookie: cookieHeader,
                // The wand website origin lets web's ShimCaller classify this server-side exchange as
                // `web_wand` instead of `unknown` in the shim-traffic metric.
                Origin: new URL(wandWebsiteUrl).origin,
            },
        });

        if (!response.ok) {
            const message = await response.text();
            return {
                ok: false,
                error: `Refresh token failed: ${response.status} ${message}`,
            };
        }

        const json = (await response.json()) as { accessToken?: unknown };
        const bearerToken = json.accessToken;

        if (!bearerToken || typeof bearerToken !== 'string') {
            return {
                ok: false,
                error: 'Bearer token is missing or invalid',
            };
        }

        return {
            ok: true,
            token: bearerToken,
            setCookieHeaders: extractSetCookieHeaders(response),
            response,
        };
    } catch (err) {
        return {
            ok: false,
            error: err instanceof Error ? err.message : 'Unknown error occurred',
        };
    }
}
